import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Close } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 30,
    overflow: "hidden"
  },
  header: {
    padding: theme.spacing(2),
    background: "linear-gradient(135deg,#071B4D,#0066FF,#00A3FF)",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  title: {
    fontWeight: 950,
    fontSize: "1.2rem"
  },
  subtitle: {
    fontSize: ".78rem",
    opacity: .85,
    marginTop: 4
  },
  closeBtn: {
    width: 38,
    height: 38,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,.25)",
    background: "rgba(255,255,255,.14)",
    color: "#fff",
    cursor: "pointer"
  },
  steps: {
    display: "grid",
    gridTemplateColumns: "repeat(4,1fr)",
    gap: theme.spacing(.8),
    padding: theme.spacing(1.4),
    background: "#F8FAFC",
    borderBottom: "1px solid #E2E8F0",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr"
    }
  },
  step: {
    borderRadius: 16,
    padding: theme.spacing(1),
    border: "1px solid #E2E8F0",
    background: "#fff",
    fontWeight: 900,
    fontSize: ".75rem",
    color: "#64748B",
    textAlign: "center"
  },
  stepActive: {
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    borderColor: "transparent",
    boxShadow: "0 10px 24px rgba(0,102,255,.20)"
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 16
    }
  },
  imageBox: {
    borderRadius: 26,
    minHeight: 260,
    border: "2px dashed rgba(0,102,255,.25)",
    background: "linear-gradient(135deg,#F8FAFC,#EEF5FF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    cursor: "pointer",
    overflow: "hidden"
  },
  previewImg: {
    width: "100%",
    height: 280,
    objectFit: "cover"
  },
  hintTitle: {
    fontWeight: 950,
    color: "#0066FF",
    fontSize: ".92rem"
  },
  hintSub: {
    color: "#64748B",
    fontSize: ".74rem",
    marginTop: 5
  },
  pill: {
    borderRadius: 999,
    fontWeight: 900
  },
  premiumBox: {
    borderRadius: 22,
    padding: theme.spacing(1.4),
    background: "linear-gradient(135deg,#EEF5FF,#FFFFFF)",
    border: "1px solid rgba(0,102,255,.14)"
  },
  footer: {
    padding: theme.spacing(1.5),
    borderTop: "1px solid #E2E8F0"
  },
  nextBtn: {
    borderRadius: 16,
    fontWeight: 950,
    textTransform: "none",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    padding: theme.spacing(1, 2),
    boxShadow: "0 12px 26px rgba(0,102,255,.22)",
    "&:hover": {
      background: "linear-gradient(135deg,#0052CC,#007ACC)"
    }
  }
}));

const emptyForm = {
  type: "product",
  name: "",
  subtitle: "",
  description: "",
  category: "",
  price: "",
  promoPrice: "",
  durationMinutes: "",
  stock: "",
  sku: "",
  imageUrl: "",
  externalUrl: "",
  ctaText: "Tenho interesse",
  tags: "",
  aiPrompt: "",
  postChoiceMode: "continue",
  postChoicePrompt: "",
  postChoiceLink: "",
  featured: false,
  active: true
};

const steps = [
  "Dados",
  "Foto",
  "Preço",
  "IA & Publicação"
];

const CatalogWizardModal = ({ open, onClose, item, reload, categories = [] }) => {
  const classes = useStyles();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const linkedService = Boolean(item?.serviceId);

  useEffect(() => {
    if (item) {
      setForm({
        ...emptyForm,
        ...item,
        postChoiceMode: item.postChoiceMode || "continue",
        postChoicePrompt: item.postChoicePrompt || "",
        postChoiceLink: item.postChoiceLink || "",
        tags: (() => {
          try {
            const parsed = JSON.parse(item.tags || "[]");
            return Array.isArray(parsed) ? parsed.join(", ") : item.tags || "";
          } catch {
            return item.tags || "";
          }
        })()
      });
    } else {
      setForm(emptyForm);
    }
    setStep(0);
  }, [item, open]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const categoryOptions = Array.from(new Set([
    ...categories,
    "Produtos",
    "Serviços",
    "Combos",
    "Promoções",
    "Mais vendidos",
    "Lançamentos",
    "Acessórios",
    "Premium"
  ].filter(Boolean)));

  const uploadImage = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Envie apenas imagens.");
      return;
    }

    try {
      setUploading(true);
      const dataFile = new FormData();
      dataFile.append("file", file);

      const { data } = await api.post("/catalog-items/upload-image", dataFile, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      set("imageUrl", data.imageUrl);
      toast.success("Imagem adicionada.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do item.");
      setStep(0);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        price: form.price || 0,
        promoPrice: form.promoPrice || null,
        durationMinutes: form.type !== "product" ? form.durationMinutes || 30 : null,
        stock: form.type === "product" ? form.stock || 0 : null,
        tags: form.tags
          ? String(form.tags).split(",").map(t => t.trim()).filter(Boolean)
          : []
      };

      if (item?.id) await api.put(`/catalog-items/${item.id}`, payload);
      else await api.post("/catalog-items", payload);

      toast.success(item?.id ? "Item atualizado." : "Item criado.");
      reload();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    if (step === 0) {
      return (
        <Grid container spacing={2}>
          {linkedService && (
            <Grid item xs={12}>
              <div className={classes.premiumBox}>
                <Typography style={{ fontWeight: 950, color: "#0F172A" }}>
                  Serviço sincronizado com a Agenda
                </Typography>
                <Typography style={{ color: "#64748B", fontSize: ".78rem", marginTop: 6 }}>
                  Nome, descrição, preço, duração e disponibilidade são administrados pela Agenda. Aqui você personaliza somente a apresentação comercial e o comportamento da Korax IA.
                </Typography>
              </div>
            </Grid>
          )}

          {!linkedService && (
            <>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" fullWidth className={classes.field}>
                  <InputLabel>Tipo</InputLabel>
                  <Select value={form.type} label="Tipo" onChange={e => set("type", e.target.value)}>
                    <MenuItem value="product">Produto</MenuItem>
                    <MenuItem value="service">Serviço</MenuItem>
                    <MenuItem value="combo">Combo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField className={classes.field} variant="outlined" label="Nome do item" fullWidth value={form.name} onChange={e => set("name", e.target.value)} />
              </Grid>
            </>
          )}

          {linkedService && (
            <Grid item xs={12}>
              <TextField className={classes.field} variant="outlined" label="Serviço" fullWidth value={form.name} disabled />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField className={classes.field} variant="outlined" label="Subtítulo curto" fullWidth value={form.subtitle || ""} onChange={e => set("subtitle", e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={categoryOptions}
              value={form.category || ""}
              onChange={(e, value) => set("category", value || "")}
              onInputChange={(e, value) => set("category", value || "")}
              renderInput={params => (
                <TextField {...params} className={classes.field} variant="outlined" label="Categoria" fullWidth helperText="Escolha uma categoria ou digite uma nova" />
              )}
            />
          </Grid>

          {!linkedService && (
            <Grid item xs={12}>
              <TextField className={classes.field} variant="outlined" label="Descrição" fullWidth multiline rows={4} value={form.description || ""} onChange={e => set("description", e.target.value)} />
            </Grid>
          )}

          {linkedService && form.description && (
            <Grid item xs={12}>
              <TextField className={classes.field} variant="outlined" label="Descrição cadastrada na Agenda" fullWidth multiline rows={3} value={form.description || ""} disabled />
            </Grid>
          )}
        </Grid>
      );
    }

    if (step === 1) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <input id="catalog-wizard-image" type="file" accept="image/*" style={{ display: "none" }} onChange={uploadImage} />
            <label htmlFor="catalog-wizard-image" className={classes.imageBox}>
              {form.imageUrl ? (
                <img src={form.imageUrl} alt="Preview" className={classes.previewImg} />
              ) : (
                <div>
                  <div style={{ fontSize: 44, marginBottom: 8 }}>📸</div>
                  <div className={classes.hintTitle}>
                    {uploading ? "Enviando imagem..." : "Adicionar foto do item"}
                  </div>
                  <div className={classes.hintSub}>
                    Clique para escolher da galeria, câmera ou arquivos do celular
                  </div>
                </div>
              )}
            </label>
          </Grid>

          {form.imageUrl && (
            <Grid item xs={12}>
              <Button onClick={() => set("imageUrl", "")} color="secondary" variant="outlined" style={{ borderRadius: 999, fontWeight: 900, textTransform: "none" }}>
                Remover imagem
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField className={classes.field} variant="outlined" label="URL manual da imagem" fullWidth value={form.imageUrl || ""} onChange={e => set("imageUrl", e.target.value)} />
          </Grid>
        </Grid>
      );
    }

    if (step === 2) {
      return (
        <Grid container spacing={2}>
          {!linkedService && (
            <Grid item xs={12} sm={6}>
              <TextField className={classes.field} variant="outlined" label="Preço" type="number" fullWidth value={form.price || ""} onChange={e => set("price", e.target.value)} />
            </Grid>
          )}

          {linkedService && (
            <Grid item xs={12} sm={6}>
              <TextField className={classes.field} variant="outlined" label="Preço definido na Agenda" type="number" fullWidth value={form.price || ""} disabled />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField className={classes.field} variant="outlined" label="Preço promocional" type="number" fullWidth value={form.promoPrice || ""} onChange={e => set("promoPrice", e.target.value)} />
          </Grid>

          {form.type === "product" && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField className={classes.field} variant="outlined" label="Estoque" type="number" fullWidth value={form.stock || ""} onChange={e => set("stock", e.target.value)} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField className={classes.field} variant="outlined" label="SKU / Código interno" fullWidth value={form.sku || ""} onChange={e => set("sku", e.target.value)} />
              </Grid>
            </>
          )}

          {form.type !== "product" && !linkedService && (
            <Grid item xs={12} sm={6}>
              <TextField className={classes.field} variant="outlined" label="Duração em minutos" type="number" fullWidth value={form.durationMinutes || ""} onChange={e => set("durationMinutes", e.target.value)} />
            </Grid>
          )}

          {form.type !== "product" && linkedService && (
            <Grid item xs={12} sm={6}>
              <TextField className={classes.field} variant="outlined" label="Duração definida na Agenda" type="number" fullWidth value={form.durationMinutes || ""} disabled />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField className={classes.field} variant="outlined" label="Texto do botão" fullWidth value={form.ctaText || ""} onChange={e => set("ctaText", e.target.value)} />
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.premiumBox}>
            <Typography style={{ fontWeight: 950, color: "#0F172A" }}>
              ✨ Como a Korax IA deve vender este item?
            </Typography>
            <Typography style={{ color: "#64748B", fontSize: ".78rem", marginTop: 6 }}>
              Escreva instruções para a IA oferecer, explicar, vender ou agendar este item durante o atendimento.
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12}>
          <TextField className={classes.field} variant="outlined" label="Instrução para IA" fullWidth multiline rows={4} value={form.aiPrompt || ""} onChange={e => set("aiPrompt", e.target.value)} />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.premiumBox}>
            <Typography style={{ fontWeight: 950, color: "#0F172A" }}>
              🔁 Depois que o cliente escolher este item
            </Typography>
            <Typography style={{ color: "#64748B", fontSize: ".78rem", marginTop: 6 }}>
              Defina a próxima ação automática da Korax após o cliente demonstrar interesse.
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12}>
          <FormControl variant="outlined" fullWidth className={classes.field}>
            <InputLabel>Ação após escolha</InputLabel>
            <Select
              value={form.postChoiceMode || "continue"}
              label="Ação após escolha"
              onChange={e => set("postChoiceMode", e.target.value)}
            >
              <MenuItem value="continue">Continuar atendimento pelo WhatsApp</MenuItem>
              <MenuItem value="ask_question">Fazer uma pergunta</MenuItem>
              <MenuItem value="send_link">Enviar link de compra/pagamento</MenuItem>
              <MenuItem value="open_agenda">Abrir agendamento</MenuItem>
              <MenuItem value="human_handoff">Chamar humano/equipe</MenuItem>
              <MenuItem value="custom_message">Enviar mensagem personalizada</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {form.postChoiceMode !== "continue" && (
          <Grid item xs={12}>
            <TextField
              className={classes.field}
              variant="outlined"
              label={
                form.postChoiceMode === "ask_question" ? "Pergunta que a IA deve fazer" :
                form.postChoiceMode === "send_link" ? "Mensagem antes do link" :
                form.postChoiceMode === "open_agenda" ? "Mensagem antes de abrir a agenda" :
                form.postChoiceMode === "human_handoff" ? "Mensagem antes de chamar a equipe" :
                "Mensagem personalizada"
              }
              fullWidth
              multiline
              rows={3}
              value={form.postChoicePrompt || ""}
              onChange={e => set("postChoicePrompt", e.target.value)}
              placeholder={
                form.postChoiceMode === "ask_question" ? "Ex: Qual seu CEP para eu calcular o frete?" :
                form.postChoiceMode === "send_link" ? "Ex: Perfeito, você pode finalizar por este link:" :
                form.postChoiceMode === "open_agenda" ? "Ex: Perfeito, quer agendar uma avaliação?" :
                form.postChoiceMode === "human_handoff" ? "Ex: Vou chamar uma pessoa da equipe para continuar com você." :
                "Ex: Perfeito, vou te explicar os próximos passos."
              }
            />
          </Grid>
        )}

        {form.postChoiceMode === "send_link" && (
          <Grid item xs={12}>
            <TextField
              className={classes.field}
              variant="outlined"
              label="Link de compra, pagamento ou página"
              fullWidth
              value={form.postChoiceLink || ""}
              onChange={e => set("postChoiceLink", e.target.value)}
              placeholder="https://..."
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField className={classes.field} variant="outlined" label="Tags separadas por vírgula" fullWidth value={form.tags || ""} onChange={e => set("tags", e.target.value)} />
        </Grid>

        <Grid item xs={12}>
          <TextField className={classes.field} variant="outlined" label="Link externo / checkout / página" fullWidth value={form.externalUrl || ""} onChange={e => set("externalUrl", e.target.value)} />
        </Grid>

        <Grid item xs={12}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip className={classes.pill} color={form.active ? "primary" : "default"} label={form.active ? "Ativo" : "Inativo"} onClick={() => set("active", !form.active)} />
            <Chip className={classes.pill} color={form.featured ? "primary" : "default"} label={form.featured ? "Destaque" : "Marcar destaque"} onClick={() => set("featured", !form.featured)} />
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>
            {linkedService ? "Personalizar serviço no catálogo" : item?.id ? "Editar item do catálogo" : "Novo item do catálogo"}
          </Typography>
          <Typography className={classes.subtitle}>
            {linkedService
              ? "Personalize imagem, oferta, chamada e comportamento da Korax IA."
              : "Cadastre produtos, serviços e combos para o WhatsApp, Agenda e Korax IA."}
          </Typography>
        </div>

        <button className={classes.closeBtn} onClick={onClose}>
          <Close fontSize="small" />
        </button>
      </div>

      <div className={classes.steps}>
        {steps.map((s, index) => (
          <div key={s} className={`${classes.step} ${step === index ? classes.stepActive : ""}`} onClick={() => setStep(index)} style={{ cursor: "pointer" }}>
            {index + 1}. {s}
          </div>
        ))}
      </div>

      <DialogContent style={{ padding: 22 }}>
        {renderStep()}
      </DialogContent>

      <DialogActions className={classes.footer}>
        <Button disabled={saving || step === 0} onClick={() => setStep(prev => Math.max(prev - 1, 0))} style={{ borderRadius: 14, fontWeight: 900, textTransform: "none" }}>
          Voltar
        </Button>

        {step < steps.length - 1 ? (
          <Button className={classes.nextBtn} onClick={() => setStep(prev => prev + 1)}>
            Próximo
          </Button>
        ) : (
          <Button className={classes.nextBtn} disabled={saving} onClick={save}>
            {saving ? "Salvando..." : item?.id ? "Salvar alterações" : "Publicar item"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CatalogWizardModal;
